import { type FC, type Ref, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router";

import { useMutation, useQuery } from "@apollo/client/react";

import {
  ConversationMessageBuildDocument,
  DashboardConversationsDetailsDocument,
  type MessageFragment,
  MessageRoleEnum,
  MessageStatusEnum,
} from "@app_schema";

import { Status } from "@application/hooks/use_action_cable_subscription";
import { useConversationStop } from "@application/hooks/use_conversation_stop";
import { usePolling } from "@application/hooks/use_polling";

import { track } from "@application/utilities/track";

import { ActionBar } from "@styled/action_bar";
import { ButtonLink } from "@styled/button_link";
import { Chat } from "@styled/chat";
import { ChatBubble } from "@styled/chat_bubble";
import { ChatList, ChatListContainer } from "@styled/chat_list";
import { CitationsToCheck } from "@styled/citations_to_check";
import { FileList } from "@styled/file_list";
import { Loader } from "@styled/loader";

import { appendMessage } from "./append_message";
import { ConversationDestroyButton } from "./conversation_destroy_button";
import { ConversationHeader } from "./conversation_header";
import { useAnswerViewed } from "./use_answer_viewed";
import { useLiveThread } from "./use_live_thread";
import { AskAboutCitations } from "@application/components/dashboard/projects/assistant/ask_about_citations";
import { CitationsOutside } from "@application/components/dashboard/projects/assistant/drafting_sources";
import { showDraftSection } from "@application/components/dashboard/projects/assistant/use_show_draft_section";
import { ToolCallRequests } from "@application/components/dashboard/tool_call_requests";

const ROLES: Record<MessageRoleEnum, "assistant" | "user"> = {
  [MessageRoleEnum.Assistant]: "assistant",
  [MessageRoleEnum.User]: "user",
};

// `answerRef` marks the conversation's newest message, so `useAnswerViewed` can
// tell when that answer is actually on screen; only its citations ask about.
const MessageChatBubble: FC<{
  message: MessageFragment;
  answerRef?: Ref<HTMLDivElement>;
}> = ({ message, answerRef }) => (
  <div ref={answerRef}>
    <ChatBubble
      html={message.html}
      role={ROLES[message.role]}
      files={message.files}
      timestamp={message.createdAt}
      text={message.draftCopy?.text ?? message.text ?? undefined}
      copies={message.draftCopy?.sections.map((section) => ({
        label: section.heading,
        text: section.text,
        onCopy: () => showDraftSection(section.marker),
      }))}
      checks={
        message.citationsToCheck &&
        message.citationsToCheck.length > 0 && (
          <CitationsToCheck
            lines={message.citationsToCheck.map(
              ({ id, name, readsLabel, citations }) => ({
                key: id,
                line: (
                  <>
                    {name}:{" "}
                    <CitationsOutside
                      citations={citations}
                      reads={readsLabel}
                    />
                  </>
                ),
              }),
            )}
            action={answerRef && <AskAboutCitations />}
          />
        )
      }
      thinkingHtml={message.thinkingHtml}
      thinkingDuration={message.duration}
      actions={<ToolCallRequests resource={message} />}
      failed={message.status === MessageStatusEnum.Failed}
      cancelled={message.status === MessageStatusEnum.Cancelled}
      processing={message.status === MessageStatusEnum.Processing}
    />
  </div>
);

// Renders a conversation as either a standalone page (default) or embedded
// inside another surface like the project detail page. In embedded mode the
// header + action bar are suppressed and the Chat shell's own input is hidden
// — the embedding surface (e.g. the project assistant drawer) supplies its own
// input and destroy affordances.
export const DashboardConversationsDetails: FC<{
  id?: string;
  embedded?: boolean;
}> = ({ id: idProp, embedded = false }) => {
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = idProp ?? params.id;
  if (!id) throw new Error("undefined ID");

  // Shows a chat already loaded at once, and loads it fresh behind that.
  const { data, refetch, startPolling, stopPolling } = useQuery(
    DashboardConversationsDetailsDocument,
    {
      variables: { id },
      fetchPolicy: "cache-and-network",
    },
  );

  const [execute, { loading: saving }] = useMutation(
    ConversationMessageBuildDocument,
    {
      update: (cache, { data }, { variables }) => {
        if (variables) {
          appendMessage(cache, variables.id, data?.conversation.chat.message);
        }
      },
    },
  );

  const chat = async (input: { message: string; fileIDs?: string[] }) => {
    const result = await execute({
      variables: {
        id,
        input: {
          text: input.message,
          fileIDs: input.fileIDs,
        },
      },
    });
    if (!result.error) {
      track("assistant_message_sent", { conversation_id: id });
    }
    return result;
  };

  const status = useLiveThread({
    id,
    messageIds: data?.conversation?.messages?.map((m) => m.id) ?? [],
    refetch,
  });

  const hasProcessingMessage =
    data?.conversation?.messages?.some(
      (m) => m.status === MessageStatusEnum.Processing,
    ) ?? false;

  // The channel refetches on every change; poll only while it's down.
  usePolling({
    startPolling,
    stopPolling,
    poll: hasProcessingMessage && status !== Status.Connected,
    interval: 3000,
  });

  const { running, stopping, stop } = useConversationStop(data?.conversation);

  const scrollRef = useRef<HTMLDivElement>(null);
  const messages = data?.conversation?.messages ?? [];
  const lastMessage = messages[messages.length - 1];
  const answerRef = useAnswerViewed({
    conversationId: id,
    message: lastMessage,
  });

  // Embedded chat panel: pin to the newest message on open and as messages
  // arrive/stream, so the latest is always in view just above the composer.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [
    messages.length,
    lastMessage?.id,
    lastMessage?.html,
    lastMessage?.status,
  ]);

  const conversation = data?.conversation;
  if (!conversation) return <Loader />;

  // Embedded (project detail chat panel): own scroll container, no Chat shell.
  // User messages render as a right-aligned indigo bubble; assistant keeps the
  // production ChatBubble pattern.
  if (embedded) {
    return (
      <div
        ref={scrollRef}
        className="min-h-0 flex-1 overflow-y-auto py-[18px] md:px-4"
      >
        <div className="flex flex-col gap-3.5">
          {messages.map((message) =>
            message.role === MessageRoleEnum.User ? (
              <div key={message.id} className="flex flex-col items-end gap-1">
                <div className="max-w-[78%] rounded-[16px_16px_4px_16px] bg-indigo-600 px-3.5 py-2 text-[13px] leading-snug whitespace-pre-wrap text-white">
                  {message.text}
                </div>
                {message.files?.length ? (
                  <FileList attachments={message.files} />
                ) : null}
              </div>
            ) : (
              <MessageChatBubble
                key={message.id}
                message={message}
                answerRef={message === lastMessage ? answerRef : undefined}
              />
            ),
          )}
        </div>
      </div>
    );
  }

  return (
    <Chat
      disabled={!conversation.policy.ask}
      inputKind={embedded ? undefined : "chat_file"}
      loading={saving}
      running={running}
      stopping={stopping}
      onStop={stop}
      chat={chat}
    >
      {!embedded && (
        <div>
          <ConversationHeader conversation={conversation} />
          <ActionBar>
            {conversation.project && (
              <ButtonLink
                color="white"
                to={`/dashboard/projects/${conversation.project.id}`}
              >
                View Project
              </ButtonLink>
            )}
            <ConversationDestroyButton
              conversation={conversation}
              onDestroy={() => navigate("/dashboard/conversations")}
            />
          </ActionBar>
        </div>
      )}
      <ChatListContainer>
        <ChatList maxWidth={embedded ? "none" : "5xl"} centered={!embedded}>
          {conversation.messages.map((message) => (
            <MessageChatBubble
              key={message.id}
              message={message}
              answerRef={message === lastMessage ? answerRef : undefined}
            />
          ))}
        </ChatList>
      </ChatListContainer>
    </Chat>
  );
};
